'use client';

import React from 'react';
import Image from 'next/image';

import ShimmerText from '@/components/ShimmerText';
import AppMockup from '@/components/AppMockup';

const Hero = () => {
  const [activeLine, setActiveLine] = React.useState<0 | 1 | 2>(1);

  React.useEffect(() => {
    let cancelled = false;

    const runSequence = async () => {
      while (!cancelled) {
        setActiveLine(1);
        await new Promise((resolve) => setTimeout(resolve, 1900));
        if (cancelled) return;

        setActiveLine(2);
        await new Promise((resolve) => setTimeout(resolve, 1900));
        if (cancelled) return;

        setActiveLine(0);
        await new Promise((resolve) => setTimeout(resolve, 1150));
      }
    };

    runSequence();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="section-dark pt-32 pb-24 relative overflow-hidden">
      {/* clean — no gradient overlay */}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid gap-12 items-center lg:grid-cols-[1fr_1.3fr]">
          <div className="text-center lg:text-left">
            <div className="eyebrow-pill mx-auto mb-6 inline-flex items-center gap-1.5 rounded-full border border-white/6 bg-white/[0.03] px-4 py-2 text-[9px] font-semibold uppercase tracking-[0.14em] text-text-secondary sm:gap-2 sm:px-5 sm:py-2.5 sm:text-[10px] lg:mx-0">
              <span className="h-1.5 w-1.5 rounded-full bg-accent-blue" />
              Private Beta
            </div>

            <h1 className="section-title mx-auto mb-6 max-w-none text-[1.7rem] leading-[1.02] font-bold tracking-tight sm:text-[2.05rem] lg:mx-0 lg:text-[2.45rem]">
              <span className="inline-flex flex-col items-center gap-1.5 lg:items-start">
                <ShimmerText
                  className="block w-fit whitespace-nowrap"
                  baseClassName="text-[#d8dde5]"
                  active={activeLine === 1}
                  duration={1.55}
                >
                  You build great digital tools.
                </ShimmerText>
                <ShimmerText
                  className="block w-fit whitespace-nowrap"
                  baseClassName="text-[#9db8c6]"
                  active={activeLine === 2}
                  duration={1.55}
                >
                  We help you find clients.
                </ShimmerText>
              </span>
            </h1>

            <p className="section-subtitle mx-auto mb-8 max-w-[34rem] text-[0.95rem] leading-relaxed text-text-secondary sm:text-base lg:mx-0">
              Pipeout finds the right companies that fit your product, scores them, finds the right people at those companies, and starts automations for you.
            </p>

            <div className="mb-10 flex flex-col items-center gap-3 sm:flex-row sm:flex-wrap sm:gap-4 lg:items-start">
              <a href="#request-access" className="btn-primary w-full max-w-[16rem] rounded-[20px] px-6 py-2.5 text-[0.95rem] font-semibold sm:w-auto sm:max-w-none sm:px-7 sm:py-3 sm:text-sm">
                Request Access
              </a>
              <a
                href="#pipeline"
                className="btn-secondary w-full max-w-[16rem] rounded-full border border-white/10 px-6 py-2.5 text-[0.95rem] font-semibold text-white hover:border-white/16 hover:bg-white/6 sm:w-auto sm:max-w-none sm:px-7 sm:py-3 sm:text-sm"
              >
                See How It Works
              </a>
            </div>
          </div>

          <div className="relative mt-10 lg:mt-0 flex justify-center lg:justify-end">
            <div className="w-full max-w-[40rem]">
              <AppMockup />
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center gap-2.5">
          <p
            className="text-center font-medium uppercase leading-none text-text-secondary/35"
            style={{ fontSize: '8.5px', letterSpacing: '0.16em' }}
          >
            Supported by
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
            <a
              href="https://scet.berkeley.edu/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-11 w-[136px] sm:h-12 sm:w-[148px] items-center justify-center rounded-2xl border border-white/4 bg-white/[0.02] px-4 opacity-75 hover:opacity-100 transition-opacity duration-140"
              aria-label="UC Berkeley SCET"
            >
              <Image
                src="/images/berkeley.png"
                alt="UC Berkeley SCET"
                width={148}
                height={32}
                className="h-auto max-h-6 w-auto max-w-[108px] object-contain"
              />
            </a>
            <a
              href="https://www.futurelab.ge/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-11 w-[136px] sm:h-12 sm:w-[148px] items-center justify-center rounded-2xl border border-white/4 bg-white/[0.02] px-4 opacity-75 hover:opacity-100 transition-opacity duration-140"
              aria-label="Future Lab"
            >
              <Image
                src="/images/futurelab.png"
                alt="Future Lab"
                width={148}
                height={32}
                className="h-auto max-h-6 w-auto max-w-[108px] object-contain"
              />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
